import { useEffect, useRef, useState } from 'react';

import type { DonationForm } from '@feathr/blackbox';
import { useStore } from '@feathr/extender/state';
import type { ListResponse } from '@feathr/rachis';

import { useFundraisingEvents } from '../../hooks';

export interface IFundraisingSummaryMetrics {
  donationTotal: number;
  growthCredits: number;
  isLoading: boolean;
}

// Delay before refetching from API to debounce rapid donations
const REFETCH_DELAY_MS = 2000;

/**
 * Aggregates summary metrics across all donation forms using stats_cache.
 * Each form's stats_cache contains pre-aggregated donation totals and growth credits.
 *
 * When a donation event arrives, triggers a debounced refetch from the backend.
 *
 * @returns Aggregated donation total, growth credits, and loading state.
 */
function useFundraisingSummaryMetrics(): IFundraisingSummaryMetrics {
  const { DonationForms } = useStore();
  const [forms, setForms] = useState<ListResponse<DonationForm> | null>(null);

  // Debounce refetch: cancel pending timeout if new donation arrives
  const refetchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const listResponse = DonationForms.list({ pagination: { page_size: 1000 } });
    setForms(listResponse);
  }, [DonationForms]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (refetchTimeoutRef.current) {
        clearTimeout(refetchTimeoutRef.current);
      }
    };
  }, []);

  // Handle donation events: debounced refetch from backend
  useFundraisingEvents({
    onDonationCreated: () => {
      // Cancel any pending refetch
      if (refetchTimeoutRef.current) {
        clearTimeout(refetchTimeoutRef.current);
      }

      // Schedule new refetch - only the last one fires
      refetchTimeoutRef.current = setTimeout(() => {
        DonationForms.refreshApiCache();
        refetchTimeoutRef.current = null;
      }, REFETCH_DELAY_MS);
    },
  });

  // Only show loading state on initial load, not during background refetches
  const isLoading = forms ? forms.isPending && !forms.hasLoadedOnce : true;

  // Cache previous values to prevent flashing to 0 during refetch
  const previousValuesRef = useRef({ donationTotal: 0, growthCredits: 0 });

  // Calculate totals from forms - no useMemo so MobX can properly track all observables
  const { donationTotal, growthCredits } = (() => {
    // Initial load - show 0
    if (!forms || (forms.isPending && !forms.hasLoadedOnce)) {
      return { donationTotal: 0, growthCredits: 0 };
    }

    // During refetch with no models, keep previous values
    if (forms.isPending && forms.models.length === 0) {
      return previousValuesRef.current;
    }

    const calculated = forms.models.reduce(
      (acc, form) => {
        const stats = form.get('stats_cache');
        return {
          donationTotal: acc.donationTotal + (stats?.donations_total ?? 0),
          growthCredits: acc.growthCredits + (stats?.growth_credits ?? 0),
        };
      },
      { donationTotal: 0, growthCredits: 0 },
    );

    // Cache for next refetch
    previousValuesRef.current = calculated;
    return calculated;
  })();

  return {
    donationTotal,
    growthCredits,
    isLoading,
  };
}

export default useFundraisingSummaryMetrics;
